import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './Offers.css';

interface Offer {
  id: number;
  name: string;
  advertiser?: {
    name: string;
  };
  advertiser_id: number;
  goal_type: 'install' | 'lead' | 'sale' | 'event';
  payout: string;
  payout_currency: string;
  is_active: boolean;
  is_test: boolean;
  daily_cap?: number;
  monthly_cap?: number;
  daily_click_cap?: number;
  monthly_click_cap?: number;
  start_date?: string;
  end_date?: string;
  created_at: string;
  slug: string;
  external_source?: string;
}

const Offers: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'test'>('all');
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchOffers();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm]);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOffers({
        limit: itemsPerPage,
        page: currentPage
      });
      setOffers(response.offers);
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
        setTotalCount(response.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (offer.advertiser?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.goal_type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && offer.is_active && !offer.is_test) ||
                         (statusFilter === 'inactive' && !offer.is_active) ||
                         (statusFilter === 'test' && offer.is_test);

    return matchesSearch && matchesStatus;
  });

  const toggleStatus = async (id: number) => {
    const offer = offers.find(o => o.id === id);
    if (!offer) return;

    try {
      await apiService.updateOffer(id, { is_active: !offer.is_active });
      setOffers(offers.map(o =>
        o.id === id ? { ...o, is_active: !o.is_active } : o
      ));
    } catch (err: any) {
      alert(`Failed to update offer status: ${err.message}`);
    }
  };

  const handleView = (id: number) => {
    navigate(`/offers/view/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/offers/edit/${id}`);
  };

  const handleAddNew = () => {
    navigate('/offers/add');
  };

  const getStatusDisplay = (offer: Offer) => {
    if (offer.is_test) return { text: 'Test', class: 'test' };
    if (offer.is_active) return { text: 'Active', class: 'active' };
    return { text: 'Inactive', class: 'inactive' };
  };

  return (
    <div className="offers-page">
      <div className="page-header">
        <h1>All Offers</h1>
        <button className="btn btn-primary" onClick={handleAddNew}>
          <span className="btn-icon">➕</span>
          Add Offer
        </button>
      </div>

      <div className="offers-section">
        <div className="section-header">
          <div className="filter-tabs">
            <button
              className={`tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`tab ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active
            </button>
            <button
              className={`tab ${statusFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => setStatusFilter('inactive')}
            >
              Inactive
            </button>
            <button
              className={`tab ${statusFilter === 'test' ? 'active' : ''}`}
              onClick={() => setStatusFilter('test')}
            >
              Test
            </button>
          </div>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search offers..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          <table className="offers-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Offer Name</th>
                <th>Advertiser</th>
                <th>Goal Type</th>
                <th>Payout</th>
                <th>Daily Click Cap</th>
                <th>Monthly Click Cap</th>
                <th>Valid Period</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '20px' }}>
                    Loading offers...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
                    Error: {error}
                  </td>
                </tr>
              ) : filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '20px' }}>
                    No offers found
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer) => {
                  const status = getStatusDisplay(offer);
                  return (
                    <tr key={offer.id}>
                      <td>{offer.id}</td>
                      <td>
                        <div className="offer-name">
                          <span className="name">{offer.name}</span>
                          {offer.is_test && <span className="test-badge">TEST</span>}
                          {offer.external_source === 'D-LAB' && (
                            <span className="dlab-badge" style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#3498db',
                              color: 'white',
                              borderRadius: '3px',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}>
                              🔌 D-LAB
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{offer.advertiser?.name || `Advertiser ${offer.advertiser_id}`}</td>
                      <td>
                        <span className={`goal-type ${offer.goal_type}`}>
                          {offer.goal_type.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className="payout">
                          ${parseFloat(offer.payout).toFixed(2)} {offer.payout_currency}
                        </span>
                      </td>
                      <td>{offer.daily_click_cap != null ? offer.daily_click_cap.toLocaleString() : 'Unlimited'}</td>
                      <td>{offer.monthly_click_cap != null ? offer.monthly_click_cap.toLocaleString() : 'Unlimited'}</td>
                      <td style={{ fontSize: '12px' }}>
                        {offer.start_date && offer.end_date ? (
                          <div>
                            <div>{new Date(offer.start_date).toLocaleDateString()}</div>
                            <div style={{ color: '#95a5a6' }}>to</div>
                            <div>{new Date(offer.end_date).toLocaleDateString()}</div>
                          </div>
                        ) : (
                          'No limit'
                        )}
                      </td>
                      <td>
                        <button
                          className={`status-badge ${status.class}`}
                          onClick={() => !offer.is_test && toggleStatus(offer.id)}
                          disabled={offer.is_test}
                        >
                          {status.text}
                          {!offer.is_test && <span className="status-toggle">Toggle</span>}
                        </button>
                      </td>
                      <td>
                        <div className="action-links">
                          <button
                            type="button"
                            className="action-link"
                            onClick={() => handleEdit(offer.id)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="action-link"
                            onClick={() => handleView(offer.id)}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="table-info">
            Showing {filteredOffers.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
          </div>
          <div className="pagination">
            <button
              className="page-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="page-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Offers;
